<template>
    <div id="create-scheduled-pane" v-mdl class="mdl-card mdl-shadow--6dp">
        <div class="mdl-card__title">
            <h2 class="mdl-card__title-text">
                Create Scheduled Message
            </h2>
        </div>

        <div class="mdl-card__supporting-text">
            <ContactEntryBar :on-contact-list-changed="onContactListChanged" />
            <div class="mdl-textfield mdl-js-textfield">
                <textarea id="message" v-model="message" class="mdl-textfield__input"></textarea>
                <label class="mdl-textfield__label" for="message">Message text...</label>
            </div>

            <!-- PULS-1424: an image, sent by the phone as MMS, as Android's scheduled messages allow -->
            <div v-if="imagesEnabled" class="attachment">
                <input ref="imageInput" type="file" accept="image/*" class="image-input" @change="imageChosen">
                <template v-if="imagePreview">
                    <img :src="imagePreview" class="image-preview" :alt="$t('scheduled.image')">
                    <span v-if="imageScheduled" class="image-scheduled">{{ $t('scheduled.image_scheduled') }}</span>
                    <button v-else class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="removeImage">
                        {{ $t('scheduled.remove_image') }}
                    </button>
                </template>
                <button v-else class="attach-button mdl-button mdl-js-button mdl-js-ripple-effect" :disabled="preparingImage" @click="chooseImage">
                    {{ preparingImage ? $t('scheduled.preparing_image') : $t('scheduled.attach_image') }}
                </button>
            </div>
            Time: <flat-pickr v-model="timestamp" class="time-picker" :config="config" placeholder="Select a date" />
            <select v-model="repeat" class="repeat">
                <option v-for="option in repeatOptions" :key="option.value" :value="option.value">
                    {{ option.text }}
                </option>
            </select>
        </div>

        <div class="mdl-card__actions mdl-card--border">
            <button id="create" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="create">
                Create
            </button>
            <button id="cancel" class="mdl-button mdl-js-button mdl-js-ripple-effect" @click="cancel">
                Cancel
            </button>
        </div>

        <transition name="loading-fade">
            <div v-if="loading" class="loading-center">
                <spinner />
            </div>
        </transition>
    </div>
</template>

<script>

import 'flatpickr/dist/flatpickr.css';
import { Api, Util } from '@/utils/';
import FlatPickr from 'vue-flatpickr-component';
import Spinner from '@/components/Spinner.vue';
import ContactEntryBar from '../Compose/ContactEntryBar.vue';

// Off until Android fixes sending images scheduled from another device (its "secondary device"
// path in ScheduledMessageJob): the recipient gets nothing, and the sent copy synced back is empty.
// Images that are already scheduled still show and can be edited.
const SCHEDULED_IMAGES_ENABLED = false;

export default {
    name: 'CreateScheduledMessage',

    components: {
        Spinner,
        FlatPickr,
        ContactEntryBar
    },

    data () {
        return {
            to: "",
            title: "",
            message: "",
            imagesEnabled: SCHEDULED_IMAGES_ENABLED,
            image: null,
            imagePreview: null,
            preparingImage: false,
            // Set once the image's scheduled message exists, so retrying after the text part
            // failed doesn't schedule the image again.
            imageScheduled: false,
            loading: false,
            timestamp: Date.now(),
            config: {
                enableTime: true,
                defaultDate: Math.floor(Date.now())
            },
            repeat: '0',
            repeatOptions: [
                { text: this.$t('scheduled.repeat.never'), value: '0' },
                { text: this.$t('scheduled.repeat.daily'), value: '1' },
                { text: this.$t('scheduled.repeat.weekly'), value: '2' },
                { text: this.$t('scheduled.repeat.monthly'), value: '3' },
                { text: this.$t('scheduled.repeat.yearly'), value: '4' }
            ]
        };
    },

    mounted () {
        this.$store.commit("loading", false);
        this.$store.commit('title', "");
    },

    beforeDestroy () {
        this.removeImage();
    },

    methods: {
        onContactListChanged (list) {
            this.to = "";
            this.title = "";

            list.forEach((item, index) => {
                if (index == list.length - 1) {
                    this.to += item.phone;
                    if (item.name) {
                        this.title += item.name;
                    } else {
                        this.title += item.phone;
                    }
                } else {
                    this.to += item.phone + ", ";
                    if (item.name) {
                        this.title += item.name + ", ";
                    } else {
                        this.title += item.phone + ", ";
                    }
                }
            });
        },
        chooseImage () {
            this.$refs.imageInput.click();
        },

        // Compressed now rather than on Create, so a file that can't be sent is reported straight away.
        imageChosen (e) {
            const file = e.target.files && e.target.files[0];
            e.target.value = ''; // Choosing the same file again still fires change
            if (!file) {
                return;
            }

            this.preparingImage = true;
            Api.messages.media.prepare(file)
                .then(prepared => {
                    this.removeImage();
                    this.image = prepared;
                    this.imagePreview = URL.createObjectURL(prepared);
                })
                .catch(err => Util.snackbar(this.$t(err.message)))
                .finally(() => {
                    this.preparingImage = false;
                });
        },

        removeImage () {
            if (this.imagePreview) {
                URL.revokeObjectURL(this.imagePreview);
            }
            this.image = null;
            this.imagePreview = null;
        },

        create () {
            if (this.loading) {
                return;
            }

            if (this.to == '') {
                Util.snackbar("Please input a contact!");
                return;
            } else if (this.message.trim() === '' && !this.image && !this.imageScheduled) {
                Util.snackbar("Please input a message!");
                return;
            }

            this.loading = true;
            const time = Math.floor(new Date(this.timestamp));

            // As Android: text and an image are scheduled as two messages for the same time. The image
            // goes first so a failed upload leaves nothing half-scheduled.
            this.createImageMessage(time)
                .then(() => {
                    if (this.message.trim() !== '') {
                        return Api.scheduledMessages.create(this.to, this.message, time, this.title, this.repeat);
                    }
                })
                .then(() => this.handleCreated())
                .catch(() => {
                    this.loading = false;
                    Util.snackbar(this.$t(this.imageScheduled ? 'scheduled.text_failed' : 'scheduled.create_failed'));
                });
        },

        // Uploads the image as scheduled_<id>, then schedules a message pointing at it.
        createImageMessage (time) {
            if (!this.image || this.imageScheduled) {
                return Promise.resolve();
            }

            const id = Api.generateId();
            const name = 'scheduled_' + id;
            const mimeType = this.image.type || 'image/jpeg';
            return Api.messages.media.upload(this.image, name)
                .then(() => Api.scheduledMessages.create(this.to, name, time, this.title, this.repeat, mimeType, id))
                .then(() => {
                    this.imageScheduled = true;
                });
        },

        handleCreated () {
            this.loading = false;
            this.$router.push({ name: 'scheduled-messages'});
        },

        cancel () {
            this.$router.push({ name: 'scheduled-messages'});
        }
    }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
    @import "../../assets/scss/_vars.scss";

    #create-scheduled-pane {
        position: relative;
        width: 330px;
        height: 100%;
        margin: 5em auto;
    }

    .time-picker {
        border: transparent;
        font-size: 15px;
        margin-left: 12px;
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .repeat {
        margin-top: 10px;
        margin-bottom: 10px;
    }

    .attachment {
        display: flex;
        align-items: center;
        margin-bottom: 8px;

        .image-input {
            display: none;
        }

        .attach-button {
            margin-left: -16px;
        }

        .image-scheduled {
            font-size: 12px;
        }

        .image-preview {
            max-width: 120px;
            max-height: 120px;
            margin-right: 24px;
            border-radius: 4px;
        }
    }

    .loading-center {
        position: absolute;
        margin: 57px auto;
        background: #fff;

        height: 100%;
        width: 100%;

        text-align: center;
        vertical-align: middle;

        .spinner {
            margin: 35% auto;
            translate: scale(2);
        }
    }

    /* loading-fade transition */
    .loading-fade-enter-active {
        transition-delay: 1s;
        transition: all $anim-time ease;
    }
    .loading-fade-leave-active {
        transition-delay: 1s;
        transition: all $anim-time ease;
    }
    .loading-fade-enter, .loading-fade-leave-to {
        transform: translateY(70%);
        opacity: 0;
    }

    body.dark {
        #create-scheduled-pane {
            background: $bg-darker;

            .mdl-card__title {
                color: #fff;
            }

            .mdl-textfield__input, .mdl-textfield__label, .mdl-card__supporting-text {
                color: #fff;
            }

            .time-picker {
                background: $bg-darker;
                color: #fff;
            }

            .mdl-button {
                color: #fff;
            }
        }

        .loading-center {
            background: $bg-darker;
        }
    }

</style>
